import { Fragment, useCallback, useMemo, useState } from 'react';
import { __MUNICIPALITY_API_URL__ } from 'shell/constants/environment';
import Table from 'shell/components/shared/table';
import { useHttpFetch } from 'shell/hooks/use-old-http-fetch';
import FiltersTerritoryList from 'system/geographic/components/territory/list';
import { HttpResponse, HttpResponseList } from 'shell/models/http-response';
import { useEffect } from 'react';
import { Territory } from 'shell/models/territory';
import { FiltersFormTerritory, TerritoryClient } from './types';
import { useFormik } from 'formik';
import { transformDataset } from './utils';
import { useFilterTable } from 'shell/hooks/use-filter-table';
import { initialValuesTerritory } from '..';
import { AlertModal } from 'system/components/shared/delete-alert/types';
import { useNavigate } from 'react-router-dom';
import DeleteAlertModal from 'system/components/shared/delete-alert';
import DeleteSuccessModal from 'system/components/shared/delete-success';
import Breadcrumbs from 'shell/components/shared/breadcrumb';
import { CommonTerritoryCrumbs } from '../constants';
import { useActionAuthorization } from 'shell/hooks/use-action-authorization';
import ListHeader from 'shell/components/shared/list-header';
import { headCells, validationSchemaTerritoryFilter, authsObj } from './constants';
import { Typography } from '@mui/material';

const TerritoryListPage = (): React.ReactElement => {
  const [deleteAlertModal, setDeleteAlertModal] = useState<AlertModal<TerritoryClient>>({
    element: null,
    open: false,
  });

  const [deleteSuccessModal, setDeleteSuccessModal] = useState<AlertModal<TerritoryClient>>({
    element: null,
    open: false,
  });

  const filterTable = useFilterTable<FiltersFormTerritory>();
  const {
    data,
    get: getTerritoryList,
    error,
  } = useHttpFetch<HttpResponseList<Territory>>(`${__MUNICIPALITY_API_URL__}/geo/territorio`, {
    data: {
      payload: {
        list: [],
      },
    },
  });

  const { delete: deleteTerritory } = useHttpFetch<HttpResponse<Territory>>(
    `${__MUNICIPALITY_API_URL__}/geo/territorio`
  );

  const territoryList: Array<TerritoryClient> = useMemo(() => {
    if (!!error || !data?.payload) {
      return [];
    }
    return data.payload.list.map((territory) => ({
      code: territory.key.codice,
      endonym: territory.endonimo,
      exonym: territory.esonimo,
      state: territory.codiceStato,
    }));
  }, [data]);

  const onSubmit = useCallback(
    (formValues: FiltersFormTerritory) => {
      const queryString = transformDataset({ ...formValues, page: 0 });
      filterTerritoryForm.setFieldValue('page', 0);
      getTerritoryList(queryString);
    },
    [getTerritoryList]
  );

  const filterTerritoryForm = useFormik({
    initialValues: initialValuesTerritory,
    validationSchema: validationSchemaTerritoryFilter,
    onSubmit,
  });

  useEffect(() => {
    const queryString = transformDataset(filterTable.values || initialValuesTerritory);
    getTerritoryList(queryString);
    filterTerritoryForm.setValues(filterTable.values || initialValuesTerritory);
  }, []);

  useEffect(() => {
    return () => filterTable.setValues(filterTerritoryForm.values);
  }, [filterTerritoryForm.values]);

  const count: number = useMemo(() => {
    if (!!error || !data?.payload || !data?.payload.pageInfo?.totalCount) {
      return 0;
    }
    return data.payload.pageInfo?.totalCount;
  }, [error, data?.payload]);

  const navigate = useNavigate();

  const auths = useActionAuthorization(authsObj);

  const onSort = useCallback(
    (orders) => {
      const newDataset = { ...filterTerritoryForm.values, orders };
      const queryString = transformDataset(newDataset);
      return getTerritoryList(queryString).then(() => {
        filterTerritoryForm.setFieldValue('orders', orders);
      });
    },
    [filterTerritoryForm.setFieldValue, getTerritoryList, filterTerritoryForm.values]
  );

  const onVisibilityPress = useCallback(
    (cell): Promise<TerritoryClient> => {
      return new Promise((resolve) => {
        navigate(`/sistema/geografiche/estero/territorio/dettaglio/${cell.code}`);
        resolve(cell);
      });
    },
    [navigate]
  );

  const onEditPress = useCallback(
    (cell): Promise<TerritoryClient> => {
      return new Promise((resolve) => {
        navigate(`/sistema/geografiche/estero/territorio/modifica/${cell.code}`);
        resolve(cell);
      });
    },
    [navigate]
  );

  const onTrashPress = useCallback(
    (cell): Promise<TerritoryClient> => {
      return new Promise((resolve) => {
        setDeleteAlertModal({ open: true, element: cell });
        resolve(cell);
      }) as Promise<TerritoryClient>;
    },
    [setDeleteAlertModal]
  );

  const handleChangePage = useCallback(
    (page) => {
      const newDataset = { ...filterTerritoryForm.values, page: page || 0 };
      const queryString = transformDataset(newDataset);
      return getTerritoryList(queryString).then(() => {
        filterTerritoryForm.setFieldValue('page', page);
      });
    },
    [filterTerritoryForm.values, getTerritoryList, filterTerritoryForm.setFieldValue]
  );

  const handleChangeRowsPerPage = useCallback(
    (rowsPerPage) => {
      const newDataset = {
        ...filterTerritoryForm.values,
        rowsPerPage: rowsPerPage || 0,
        page: 0,
      };

      const queryString = transformDataset(newDataset);
      return getTerritoryList(queryString).then(() => {
        filterTerritoryForm.setValues(newDataset);
      });
    },
    [filterTerritoryForm.setValues, filterTerritoryForm.values, getTerritoryList]
  );

  const onCloseDeleteAlertModal = useCallback(() => {
    setDeleteAlertModal({ open: false, element: null });
  }, [setDeleteAlertModal]);

  const onYesDeleteAlertModal = useCallback(() => {
    deleteTerritory(`/${deleteAlertModal.element?.code}`).then(() => {
      setDeleteAlertModal({ open: false, element: null });
      setDeleteSuccessModal({ open: true, element: deleteAlertModal.element });
    });
  }, [deleteTerritory, setDeleteAlertModal, setDeleteSuccessModal, deleteAlertModal.element]);

  const onNoDeleteAlertModal = useCallback(() => {
    setDeleteAlertModal({ open: false, element: null });
  }, [setDeleteAlertModal]);

  const onCloseOnOkDeleteSuccessModal = useCallback(() => {
    const queryString = transformDataset(filterTerritoryForm.values);
    getTerritoryList(queryString).then(() => setDeleteSuccessModal({ open: false, element: null }));
  }, [getTerritoryList, filterTerritoryForm.values, setDeleteSuccessModal]);

  return (
    <Fragment>
      <Breadcrumbs items={CommonTerritoryCrumbs} />
      <ListHeader
        addBtnAriaLabel="Aggiungi nuovo territorio"
        addBtnLabel="Aggiungi nuovo territorio"
        canAdd={auths.create}
        onAddBtnClick={() => {
          navigate('/sistema/geografiche/estero/territorio/nuovo');
        }}
        title="Gestione Territori"
      />
      <div className="pmi-my-3">
        <FiltersTerritoryList filterTerritoryForm={filterTerritoryForm} />
      </div>
      {data?.payload ? (
        territoryList?.length > 0 ? (
          <Table
            onSort={onSort}
            head={headCells}
            orders={filterTerritoryForm.values.orders}
            activeActions={auths}
            page={filterTerritoryForm.values.page}
            rowsPerPage={filterTerritoryForm.values.rowsPerPage}
            body={territoryList}
            onVisibilityPress={onVisibilityPress}
            onEditPress={onEditPress}
            onTrashPress={onTrashPress}
            count={count || 0}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            ariaLabel="Lista Territori"
            isServer
          />
        ) : (
          <Typography color="secondary.main">
            Nessun elemento trovato con la ricerca effettuata.
          </Typography>
        )
      ) : undefined}
      <DeleteAlertModal
        title="Cancellazione Territorio"
        message={`Confermare la cancellazione del territorio ${
          deleteAlertModal.element?.exonym || ''
        }?`}
        open={deleteAlertModal.open}
        onClose={onCloseDeleteAlertModal}
        onYes={onYesDeleteAlertModal}
        onNo={onNoDeleteAlertModal}
      />
      <DeleteSuccessModal
        open={deleteSuccessModal.open}
        title="Cancellazione Territorio"
        message={`La cancellazione del territorio ${
          deleteSuccessModal.element?.exonym || ''
        } è avvenuta con successo`}
        onClose={onCloseOnOkDeleteSuccessModal}
        onOk={onCloseOnOkDeleteSuccessModal}
      />
    </Fragment>
  );
};

TerritoryListPage.displayName = 'Territory List page component';

export default TerritoryListPage;
